<template>
  <div class="simple-table-container" :class="{ 'financial-table-container': isFinancial }">
    <table class="simple-table" :class="tableClasses">
      <thead>
        <tr>
          <th 
            v-for="(column, idx) in computedColumns" 
            :key="`col-${idx}`"
            :class="getHeaderClass(column, idx)"
          >
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in computedData"
          :key="rowIndex"
          :class="getRowClass(row)"
        >
          <td 
            v-for="(column, colIdx) in computedColumns" 
            :key="`cell-${rowIndex}-${colIdx}`"
            :class="getCellClass(row, column, colIdx)"
          >
            <slot 
              :name="`cell-${column.key}`" 
              :value="row[column.key]" 
              :row="row"
              :column="column"
            >
              {{ formatValue(row[column.key], column) }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="computedData.length === 0" class="empty-state">
      <p>No data available</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SimpleTable',
  props: {
    data: {
      type: [Array, Object],
      required: true,
      default: () => []
    },
    columns: {
      type: Array,
      default: null
    },
    expandArrays: {
      type: Boolean,
      default: false
    },
    noRowBorders: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'financial'].includes(value)
    }
  },
  computed: {
    isFinancial() {
      return this.variant === 'financial';
    },
    tableClasses() {
      return {
        'no-row-borders': this.noRowBorders,
        'financial-table': this.isFinancial
      };
    },
    computedColumns() {
      if (this.columns && this.columns.length > 0) {
        return this.columns;
      }
      
      // Handle {columns: [...], data: {...}} format — labels are display-ready from API
      if (this.data && typeof this.data === 'object' && !Array.isArray(this.data) && this.data.columns) {
        return this.data.columns.map((col, idx) => ({
          key: col,
          label: col || `Column ${idx + 1}`
        }));
      }
      
      // Auto-generate from array data
      if (Array.isArray(this.computedData) && this.computedData.length > 0) {
        return Object.keys(this.computedData[0]).map(key => ({
          key: key,
          label: this.formatLabel(key)
        }));
      }
      
      return [];
    },
    computedData() {
      if (!this.expandArrays) {
        return Array.isArray(this.data) ? this.data : [];
      }
      
      // Handle {columns: [...], data: {...}} format
      if (this.data && typeof this.data === 'object' && !Array.isArray(this.data)) {
        if (this.data.columns && this.data.data) {
          return this.tableToRecords(this.data);
        }
      }
      
      return Array.isArray(this.data) ? this.data : [];
    }
  },
  methods: {
    getHeaderClass(column, colIdx) {
      const classes = [column.class].filter(Boolean);
      if (!this.isFinancial) return classes;

      const kind = this.getColumnKind(column, colIdx);
      classes.push(`fin-col-${kind}`);
      if (colIdx === 0) classes.push('fin-col-sticky');
      return classes;
    },
    getRowClass(row) {
      if (!this.isFinancial) return [];

      const label = this.getRowLabel(row);
      if (!label) return ['fin-row-spacer'];
      if (this.isFinancialTotalRow(label)) return ['fin-row-total'];
      if (this.isFinancialSectionRow(label)) return ['fin-row-section'];
      if (this.isFinancialSubsectionRow(label)) return ['fin-row-subsection'];
      return ['fin-row-item'];
    },
    getCellClass(row, column, colIdx) {
      const classes = [column.class].filter(Boolean);
      if (!this.isFinancial) return classes;

      const kind = this.getColumnKind(column, colIdx);
      const value = row[column.key];
      classes.push(`fin-col-${kind}`);

      if (colIdx === 0) classes.push('fin-col-sticky');

      if (kind === 'amount') {
        classes.push('fin-cell-amount');
        if (this.isEmptyValue(value)) classes.push('fin-cell-empty');
        else if (this.isNegativeValue(value)) classes.push('fin-cell-negative');
      }

      return classes;
    },
    getRowLabel(row) {
      const key = this.computedColumns[0] && this.computedColumns[0].key;
      if (!key) return '';
      return String(row[key] || '').trim();
    },
    getColumnKind(column, colIdx) {
      const header = String(column.label || column.key || '').trim();
      const headerLower = header.toLowerCase();

      if (
        colIdx === 0
        || /^(particulars|category|asset category|timing|geographical markets|year)$/i.test(header)
      ) {
        return 'label';
      }

      if (/^notes?$/i.test(headerLower)) return 'note';

      if (
        /\(\s*(aed|usd|gbp|eur)\s*\)/i.test(header)
        || /\b\d{4}\b/.test(header)
        || /(amount|balance|ratio|due within|more than|as previously reported|reclassification|as reclassified)/i.test(header)
      ) {
        return 'amount';
      }

      if (/useful life|asset category|particulars|note/i.test(headerLower)) return 'text';

      return 'text';
    },
    isFinancialTotalRow(label) {
      return /^total\b/i.test(label);
    },
    isFinancialSectionRow(label) {
      if (!/[A-Za-z]/.test(label)) return false;
      const normalized = label.replace(/[^A-Za-z]/g, '');
      if (!normalized) return false;
      return label === label.toUpperCase() && normalized.length > 3;
    },
    isFinancialSubsectionRow(label) {
      const subsections = [
        'non-current',
        'current',
        'equity',
        'liabilities',
        'assets',
        'investing activity',
        'investing activities',
        'financing activities',
        'operating activities'
      ];
      const normalized = label.toLowerCase();
      return subsections.includes(normalized);
    },
    isNegativeValue(value) {
      const text = String(value || '').trim();
      return text.startsWith('(') && text.endsWith(')');
    },
    isEmptyValue(value) {
      if (value == null) return true;
      const text = String(value).trim();
      return text === '' || text === '-';
    },
    formatLabel(key) {
      if (key == null) return '';
      const str = String(key);
      if (!str.trim()) return '';
      
      return str
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    },
    formatValue(value, column) {
      if (this.isEmptyValue(value)) {
        return this.isFinancial ? '' : (column.emptyValue || '—');
      }
      if (column.formatter && typeof column.formatter === 'function') {
        return column.formatter(value);
      }
      return value;
    },
    tableToRecords(table) {
      const { columns, data } = table;
      if (!columns || !data || columns.length === 0) return [];
      
      const firstCol = columns[0];
      const rowCount = Array.isArray(data[firstCol]) ? data[firstCol].length : 0;
      
      return Array.from({ length: rowCount }, (_, i) => {
        const record = {};
        columns.forEach(col => {
          record[col] = Array.isArray(data[col]) ? (data[col][i] ?? null) : null;
        });
        return record;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../index.scss';

.simple-table-container {
  width: 100%;
  overflow-x: auto;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.simple-table {
  width: 100%;
  border-collapse: collapse;
  font-family: $brand-font-regular;
  
  thead {
    background: #f8f9fa;
    border-bottom: 2px solid #e2e8f0;
    
    th {
      padding: 0.75rem 1rem;
      text-align: left;
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d3748;
      font-family: $brand-font-medium;
      white-space: nowrap;
      
      &:first-child {
        padding-left: 1.5rem;
      }
      
      &:last-child {
        padding-right: 1.5rem;
      }
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;
      transition: background-color 0.15s ease;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
    
    td {
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
      color: #4a5568;
      font-family: $brand-font-regular;
      
      &:first-child {
        padding-left: 1.5rem;
      }
      
      &:last-child {
        padding-right: 1.5rem;
      }
    }
  }
  
  &.no-row-borders {
    tbody tr {
      border-bottom: none;
    }
  }

  &.financial-table {
    min-width: 720px;
    table-layout: auto;

    thead {
      background: #f1f5f9;
      border-bottom: 2px solid #334155;

      th {
        padding: 0.625rem 1.25rem;
        font-size: 0.8125rem;
        font-weight: 700;
        color: #0f172a;
        letter-spacing: 0.01em;
        text-transform: none;
        border-bottom: none;
        vertical-align: bottom;
        white-space: nowrap;

        &.fin-col-label {
          text-align: left;
          min-width: 240px;
        }

        &.fin-col-note {
          text-align: center;
          width: 4rem;
        }

        &.fin-col-amount {
          text-align: right;
          min-width: 7.5rem;
        }

        &.fin-col-text {
          text-align: left;
        }

        &.fin-col-sticky {
          position: sticky;
          left: 0;
          z-index: 2;
          background: #f1f5f9;
          box-shadow: 1px 0 0 #cbd5e1;
        }
      }
    }

    tbody {
      tr {
        border-bottom: none;
        transition: none;

        &:hover {
          background-color: transparent;
        }

        &.fin-row-spacer {
          td {
            padding-top: 0.35rem;
            padding-bottom: 0.35rem;
            border-bottom: none;
          }
        }

        &.fin-row-section {
          td {
            padding-top: 0.85rem;
            padding-bottom: 0.45rem;
            background: #f8fafc;
            border-top: 1px solid #cbd5e1;
            border-bottom: 1px solid #e2e8f0;
            font-family: $brand-font-medium;
            font-weight: 700;
            font-size: 0.8125rem;
            letter-spacing: 0.04em;
            color: #0f172a;
          }

          .fin-col-amount,
          .fin-col-note,
          .fin-col-text {
            color: transparent;
          }
        }

        &.fin-row-subsection {
          td {
            padding-top: 0.55rem;
            padding-bottom: 0.35rem;
            background: #fcfdfe;
            font-family: $brand-font-medium;
            font-weight: 600;
            color: #334155;
          }

          .fin-col-label {
            padding-left: 1.75rem;
          }
        }

        &.fin-row-total {
          td {
            padding-top: 0.55rem;
            padding-bottom: 0.55rem;
            border-top: 1px solid #64748b;
            border-bottom: 3px double #64748b;
            background: #f8fafc;
            font-family: $brand-font-medium;
            font-weight: 700;
            color: #0f172a;
          }
        }

        &.fin-row-item {
          td {
            padding-top: 0.4rem;
            padding-bottom: 0.4rem;
            border-bottom: 1px solid #eef2f7;
          }

          .fin-col-label {
            padding-left: 2.25rem;
            font-weight: 400;
            color: #334155;
            white-space: normal;
          }
        }
      }

      td {
        padding: 0.4rem 1.25rem;
        font-size: 0.8125rem;
        color: #1e293b;
        vertical-align: top;

        &.fin-col-sticky {
          position: sticky;
          left: 0;
          z-index: 1;
          background: inherit;
          box-shadow: 1px 0 0 #eef2f7;
        }

        &.fin-col-note {
          text-align: center;
          color: #64748b;
          font-size: 0.75rem;
          width: 4rem;
        }

        &.fin-col-amount {
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          min-width: 7.5rem;
        }

        &.fin-col-text {
          text-align: left;
          white-space: normal;
        }

        &.fin-cell-empty {
          color: transparent;
        }

        &.fin-cell-negative {
          color: #991b1b;
        }
      }

      tr.fin-row-section td.fin-col-sticky,
      tr.fin-row-total td.fin-col-sticky {
        background: #f8fafc;
      }

      tr.fin-row-subsection td.fin-col-sticky {
        background: #fcfdfe;
      }

      tr.fin-row-item td.fin-col-sticky {
        background: #ffffff;
      }
    }
  }
}

.financial-table-container {
  border: none;
  border-radius: 0;
  background: transparent;
}

.empty-state {
  padding: 3rem 2rem;
  text-align: center;
  color: #718096;
  font-size: 0.875rem;
  font-family: $brand-font-regular;
  
  p {
    margin: 0;
  }
}

@media (max-width: 768px) {
  .simple-table {
    thead th,
    tbody td {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      
      &:first-child {
        padding-left: 1rem;
      }
      
      &:last-child {
        padding-right: 1rem;
      }
    }
  }
}
</style>
